import { useEffect, useMemo, useRef, useState } from 'react';
import { useMask } from '@/hooks';
import { getNoOnlyWhiteSpaces } from '@/utils/string';
import { UseCellPhoneProps } from './type';

export function useCellphoneInput({
  value = '',
  onUpdate,
  mask = [],
  monetary = false,
  unmaskedValue = false,
  anonymizeOnBlur,
  applyMaskCheckOwner,
  name = '',
}: UseCellPhoneProps) {
  const [maskedValue, setMaskedValue] = useState(value);
  const [anonymizationState, setAnonymizationState] = useState({
    shouldHide: false,
    wasEdited: false,
  });

  const masked = useMask(mask, monetary);
  const inputRef = useRef(null);

  const hasMask = useMemo(() => !!mask?.length || monetary, [mask, monetary]);

  const isPhoneField = anonymizeOnBlur && name.toLowerCase().includes('cellphone');

  const applyAnonymization = (val: string) => {
    const digits = val.replace(/\D/g, '');
    if (digits.length < 10) return masked(val).value;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 5)}xx-xx${digits.slice(-2)}`;
  };

  const shouldAnonymize = useMemo(
    () => anonymizeOnBlur && (anonymizationState.shouldHide || applyMaskCheckOwner) && !anonymizationState.wasEdited,
    [anonymizeOnBlur, anonymizationState.shouldHide, applyMaskCheckOwner, anonymizationState.wasEdited],
  );

  const getDisplayValue = useMemo(() => {
    if (!hasMask) return value;
    if (shouldAnonymize) return applyAnonymization(masked(value.toString()).value);
    return maskedValue;
  }, [hasMask, maskedValue, value, shouldAnonymize]);

  const clearValue = () => {
    const localMasked = masked('');
    setMaskedValue(localMasked.value);
    setAnonymizationState({ wasEdited: false, shouldHide: true });
    onUpdate('');
  };

  const updateMaskedValue = (val: string) => {
    const maskedResult = masked(val);
    setMaskedValue(maskedResult.value);

    const isAnonymized = anonymizeOnBlur && maskedResult.value.includes('x');
    if (!isAnonymized) {
      onUpdate(unmaskedValue ? maskedResult.unmaskedValue : maskedResult.value);
    }
  };

  const handleInputChange = (val: string) => {
    const cleanValue = getNoOnlyWhiteSpaces(val);

    if (!cleanValue) {
      requestAnimationFrame(clearValue);
      return;
    }

    setAnonymizationState((prev) => ({ ...prev, wasEdited: true }));

    if (!hasMask) {
      if (inputRef.current) inputRef.current.value = cleanValue;
      onUpdate(cleanValue);
      return;
    }

    updateMaskedValue(cleanValue);
  };
  const handleBlur = () => {
    setAnonymizationState((prev) => ({ ...prev, shouldHide: true }));
  };

  const handleFocus = () => {
    setAnonymizationState((prev) => ({ ...prev, shouldHide: false }));
  };

  const initializeInputState = () => {
    if (!value?.toString().length) return;

    const clean = getNoOnlyWhiteSpaces(value.toString());
    const maskedResult = masked(clean);
    setMaskedValue(maskedResult.value);

    if (anonymizeOnBlur && isPhoneField && !anonymizationState.wasEdited) {
      setAnonymizationState((prev) => ({ ...prev, shouldHide: true }));
    }
  };

  const syncMaskedFromProp = () => {
    const maskedResult = masked(value.toString());

    if (anonymizeOnBlur && !anonymizationState.wasEdited) {
      setMaskedValue(maskedResult.value);
      setAnonymizationState((prev) => ({ ...prev, shouldHide: true }));
    } else {
      handleInputChange(value.toString());
    }
  };

  useEffect(() => {
    initializeInputState();
  }, []);

  useEffect(() => {
    syncMaskedFromProp();
  }, [value, anonymizeOnBlur, anonymizationState.wasEdited]);

  useEffect(() => {
    if (!anonymizationState.wasEdited && applyMaskCheckOwner) {
      setAnonymizationState((prev) => ({ ...prev, shouldHide: true }));

      const clean = getNoOnlyWhiteSpaces(value.toString());
      const maskedResult = masked(clean);
      setMaskedValue(maskedResult.value);
    }
  }, [applyMaskCheckOwner, value, anonymizationState.wasEdited]);

  return {
    inputRef,
    getDisplayValue,
    handleInputChange,
    handleFocus,
    handleBlur,
    anonymizationState,
  };
}
